import { IconArrowRight } from '@developerz/ui';
import { A } from '@solidjs/router';
import { For, type JSX, Match, Switch } from 'solid-js';
import { t, tPlural } from '../i18n';
import { orgHref } from '../lib/org';
import type { RepoRow } from '../lib/repos';
import { ErrorState } from './ErrorState';
import { Spinner } from './Spinner';
import { buttonClass } from './ui/Button';

// The "Select repos" step's content. Presenter — the container walks `/v1/repos`.
//
// `/v1/repos` serves ENABLED repos only, so this is not a picker. An install enables
// nothing (#7802): the choosing happens on the Repos page, whose toggles read every repo
// the App can see, and the free-tier private-repo gate (#1982) refuses there. What this
// step owes the user is the ANSWER — which repos the agent will actually work on — and,
// when that is none, the way to enable one, because Continue stays off until they do.
// GitHub's install screen is second: it grants access, and enables nothing.
//
// `undefined` rows with `failed` false is IN FLIGHT; `failed` is a refusal.
// "No repository is enabled" is a claim ABOUT THE ACCOUNT and only a walk that
// LANDED empty may make it — the `<StartPlanDialog/>` rule.
export type OnboardingRepoListProps = {
  readonly repos: readonly RepoRow[] | undefined;
  readonly failed: boolean;
  readonly onRetry: () => void;
  readonly ghAppInstallUrl: string;
};

export function OnboardingRepoList(props: OnboardingRepoListProps): JSX.Element {
  const rows = (): readonly RepoRow[] => props.repos ?? [];
  return (
    <Switch
      fallback={
        <p class="onboarding__waiting">
          <Spinner size="1.05rem" />
          {t('onboarding.repos_loading')}
        </p>
      }
    >
      <Match when={props.failed}>
        <ErrorState message={t('onboarding.repos_failed')} onRetry={props.onRetry} />
      </Match>
      <Match when={props.repos !== undefined && rows().length === 0}>
        <div class="alert alert--icon" role="status">
          <p class="alert__title">{t('onboarding.repos_none')}</p>
          <p class="alert__desc">{t('onboarding.repos_none_body')}</p>
        </div>
        <A class={buttonClass({ variant: 'outline', size: 'sm' })} href={orgHref('/repos')}>
          {t('onboarding.repos_enable_cta')}
          <IconArrowRight size="0.875rem" />
        </A>
        {/* GitHub redirects back to the funnel, so this must NOT open a tab:
            the return landed in tab B while the wizard sat in tab A. */}
        <a
          class={buttonClass({ variant: 'ghost', size: 'sm' })}
          href={props.ghAppInstallUrl}
          rel="noreferrer"
        >
          {t('onboarding.repos_pick_cta')}
        </a>
        <a class={buttonClass({ variant: 'ghost', size: 'sm' })} href={orgHref('/account/billing')}>
          {t('onboarding.repos_plans_cta')}
        </a>
      </Match>
      <Match when={props.repos !== undefined && rows().length > 0}>
        <p>{tPlural('onboarding.repos_count', rows().length, { count: rows().length })}</p>
        <ul class="onboarding__repos" aria-label={t('onboarding.repos_enabled_heading')}>
          <For each={rows()}>
            {(repo) => (
              <li>
                <A href={orgHref(`/repos/${encodeURIComponent(repo.full_name)}/overview`)}>
                  {repo.full_name}
                </A>
              </li>
            )}
          </For>
        </ul>
        <a class={buttonClass({ variant: 'ghost', size: 'sm' })} href={orgHref('/repos')}>
          {t('onboarding.repos_manage_cta')}
        </a>
      </Match>
    </Switch>
  );
}
